import { topologyRoomBackedPlaneUVAABB } from "building-generation/elements/walls2/planarTopologySelectionGeometry";
import type { BuildingGraph, BuildingGraphPlanarTopology } from "building-types/building/buildingGraphTypes";
import { isAbsoluteVerticalDatum, isWallPlaneHorizontalDatum } from "building-types/datums/datumEntityTypes";
import type { Doc } from "building-types/doc";
import type { PlanarTopologyDatumBounds } from "building-types/walls2/planarTopologyDatumBoundsTypes";
import { planarTopologyDatumBoundsHasDatums } from "building-types/walls2/planarTopologyDatumBoundsTypes";
import type { AABB2 } from "geom/aabb2Types";
import { worldZToTopologyV } from "./planarTopologyVerticalDatumProjection";
import { planarTopologyPlaneUVAABB } from "./planarTopologyWallCoreResolution";

export function resolveSplitRegionUBounds(
  topology: BuildingGraphPlanarTopology,
  doc: Doc,
  bounds: PlanarTopologyDatumBounds,
  buildingGraph: BuildingGraph,
  planLevelIds?: ReadonlySet<string>,
): { readonly uMin: number; readonly uMax: number } | undefined {
  const uvAabb = topologyRoomBackedPlaneUVAABB(topology, buildingGraph, planLevelIds);
  if (!uvAabb) {
    return undefined;
  }

  if (!planarTopologyDatumBoundsHasDatums(bounds)) {
    return { uMin: uvAabb.minX, uMax: uvAabb.maxX };
  }

  let uMin = uvAabb.minX;
  let uMax = uvAabb.maxX;

  if (bounds.startHorizontalDatumId) {
    const startDatum = doc.datums[bounds.startHorizontalDatumId];
    if (
      !isWallPlaneHorizontalDatum(startDatum) ||
      startDatum.planeHash !== topology.planeHash ||
      startDatum.offset === undefined
    ) {
      return undefined;
    }
    uMin = startDatum.offset;
  }

  if (bounds.endHorizontalDatumId) {
    const endDatum = doc.datums[bounds.endHorizontalDatumId];
    if (
      !isWallPlaneHorizontalDatum(endDatum) ||
      endDatum.planeHash !== topology.planeHash ||
      endDatum.offset === undefined
    ) {
      return undefined;
    }
    uMax = endDatum.offset;
  }

  if (uMin >= uMax) {
    return undefined;
  }

  return { uMin, uMax };
}

export function resolveSplitRegionVBounds(
  topology: BuildingGraphPlanarTopology,
  doc: Doc,
  bounds: PlanarTopologyDatumBounds,
): { readonly vMin: number; readonly vMax: number } | undefined {
  const uvAabb = planarTopologyPlaneUVAABB(topology);
  if (!uvAabb) {
    return undefined;
  }

  if (!planarTopologyDatumBoundsHasDatums(bounds)) {
    return { vMin: uvAabb.minY, vMax: uvAabb.maxY };
  }

  let vMin = uvAabb.minY;
  let vMax = uvAabb.maxY;

  if (bounds.startVerticalDatumId) {
    const startDatum = doc.datums[bounds.startVerticalDatumId];
    if (!isAbsoluteVerticalDatum(startDatum) || startDatum.offset === undefined) {
      return undefined;
    }
    vMin = worldZToTopologyV(topology, startDatum.offset);
  }

  if (bounds.endVerticalDatumId) {
    const endDatum = doc.datums[bounds.endVerticalDatumId];
    if (!isAbsoluteVerticalDatum(endDatum) || endDatum.offset === undefined) {
      return undefined;
    }
    vMax = worldZToTopologyV(topology, endDatum.offset);
  }

  if (vMin >= vMax) {
    return undefined;
  }

  return { vMin, vMax };
}

export function splitRegionUBoundsToAABB2(
  topology: BuildingGraphPlanarTopology,
  uBounds: { readonly uMin: number; readonly uMax: number },
  buildingGraph: BuildingGraph,
  planLevelIds?: ReadonlySet<string>,
  vBounds?: { readonly vMin: number; readonly vMax: number },
): AABB2 | undefined {
  const uvAabb = topologyRoomBackedPlaneUVAABB(topology, buildingGraph, planLevelIds);
  if (!uvAabb) {
    return undefined;
  }

  return {
    minX: uBounds.uMin,
    minY: vBounds?.vMin ?? uvAabb.minY,
    maxX: uBounds.uMax,
    maxY: vBounds?.vMax ?? uvAabb.maxY,
  };
}

export function splitRegionUvAABB(
  topology: BuildingGraphPlanarTopology,
  doc: Doc,
  bounds: PlanarTopologyDatumBounds,
  buildingGraph: BuildingGraph,
  planLevelIds?: ReadonlySet<string>,
): AABB2 | undefined {
  const uBounds = resolveSplitRegionUBounds(topology, doc, bounds, buildingGraph, planLevelIds);
  if (!uBounds) {
    return undefined;
  }
  const vBounds = resolveSplitRegionVBounds(topology, doc, bounds);
  if (!vBounds) {
    return undefined;
  }
  return splitRegionUBoundsToAABB2(topology, uBounds, buildingGraph, planLevelIds, vBounds);
}
